import { useRxData } from 'rxdb-hooks';
import { MangoQuery, RxCollection, RxDocument } from 'rxdb/dist/types/types';
import { useCallback } from 'react';
import { useRxCountQuery } from '@hooks/useCount';
import { ISupervisorDetail, ISupervisorDetailRBDM } from '@components/supervisorList';

export type ISupervisorReturn = {
	result: RxDocument<ISupervisorDetail | ISupervisorDetailRBDM>[];
	isFetching: boolean;
	fetchMore: () => void;
	totalCount: number;
};

export interface ISupervisorListFilterProps {
	searchValue?: string;
	sortBy?: string;
	createddate?: [null | Date, null | Date];
	contractor_mobile_no?: string[];
	mdicode?: string; // Add MDI Code filter
}

export const useSupervisorRxDb = (props: ISupervisorListFilterProps): ISupervisorReturn => {
	const { searchValue = '', sortBy, createddate, contractor_mobile_no, mdicode } = props;

	const queryConstructor = useCallback(
		(collection: RxCollection<ISupervisorDetail | ISupervisorDetailRBDM>) => {
			let querySelector: MangoQuery<ISupervisorDetail | ISupervisorDetailRBDM> = {
				selector: {
					$or: [
						{
							firstname: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							lastname: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							phone: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							primary_contractor_name: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							primary_contractor_mobile_no: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							primary_contractor: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							fullName: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							membershipno: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						// Add RBDM specific search fields
						{
							created_by: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
					],
				},
				sort: [{ createddate: 'desc' }],
			};

			if (querySelector.selector && contractor_mobile_no && contractor_mobile_no.length > 0) {
				querySelector.selector.primary_contractor_mobile_no = {
					$in: contractor_mobile_no,
				};
			}

			if (querySelector.selector && createddate) {
				const [startDate, endDate] = createddate;
				if (startDate && endDate) {
					querySelector.selector.createddate = {
						$gte: startDate.toISOString(),
						$lte: endDate.toISOString(),
					};
				}
			}

			// Add MDI Code filter
			if (querySelector.selector && mdicode && mdicode.trim() !== '') {
				querySelector.selector.mdicode = {
					$eq: mdicode,
				};
			}

			if (sortBy) {
				querySelector = {
					...querySelector,
					sort: [{ [sortBy]: 'desc' }],
				};
			}

			return collection.find(querySelector);
		},
		[contractor_mobile_no, createddate, searchValue, sortBy, mdicode]
	);

	// Use the SAME collection for both user types
	const totalCount = useRxCountQuery<ISupervisorDetail | ISupervisorDetailRBDM>('supervisor_list', queryConstructor);

	const { result, isFetching, fetchMore } = useRxData<ISupervisorDetail | ISupervisorDetailRBDM>(
		'supervisor_list',
		queryConstructor,
		{
			pageSize: 20,
			pagination: 'Infinite',
		}
	);

	return {
		result,
		isFetching,
		fetchMore,
		totalCount,
	};
};
